"use client"

import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { goToDashboard } from "@/features/sso/goToDashboard";

/**
 * Setup-guide checklist (docs/codebase/onboarding/README.md §2.2).
 *
 * Reads GET /api/v1/company/onboarding (same contract as the Jinja
 * dashboard card) and shows the 6 activation steps while the salon is
 * still setting up. Hidden once activated + first booking, or when
 * dismissed (per browser).
 */

type OnboardingState = {
  steps: Record<string, boolean>;
  done: number;
  total: number;
  progress: number;
  activated: boolean;
  first_online_booking: boolean;
};

const DISMISS_KEY = "setup-guide-dismissed";

const STEP_ORDER: Array<{ key: string; path: string }> = [
  { key: "services", path: "/dashboard/services" },
  { key: "staff", path: "/dashboard/staff" },
  { key: "staff_services", path: "/dashboard/staff" },
  { key: "schedule", path: "/dashboard/staff" },
  { key: "booking_link", path: "/dashboard/settings/integration" },
  { key: "first_booking", path: "/dashboard/records" },
];

export function SetupGuideCard() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = React.useState(
    () => localStorage.getItem(DISMISS_KEY) === "1"
  );

  const { data } = useQuery<OnboardingState>({
    queryKey: ["company-onboarding"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/company/onboarding")).data,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 1,
  });

  if (!data || dismissed) return null;
  if (data.activated && data.steps.first_booking) return null;

  const labels: Record<string, string> = {
    services: t("setup_guide.step_services", "Create your service list"),
    staff: t("setup_guide.step_staff", "Add your masters"),
    staff_services: t("setup_guide.step_staff_services", "Assign services to masters"),
    schedule: t("setup_guide.step_schedule", "Fill a work schedule"),
    booking_link: t("setup_guide.step_booking_link", "Get your booking link live"),
    first_booking: t("setup_guide.step_first_booking", "Take your first booking"),
  };

  return (
    <div className="mx-4 mt-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-sm">
          {t("setup_guide.title", "Setup guide")}
        </span>
        <button
          className="text-xs text-gray-400 hover:text-gray-600"
          onClick={() => {
            localStorage.setItem(DISMISS_KEY, "1");
            setDismissed(true);
          }}
        >
          {t("setup_guide.dismiss", "Hide")}
        </button>
      </div>

      <div className="mb-1 h-1.5 w-full rounded bg-gray-100">
        <div
          className="h-1.5 rounded bg-emerald-500 transition-all"
          style={{ width: `${data.progress}%` }}
        />
      </div>
      <div className="mb-3 text-xs text-gray-500">
        {t("setup_guide.progress", "Steps completed:")} {data.done}/{data.total}
      </div>

      <ul className="space-y-1.5">
        {STEP_ORDER.map(({ key, path }) =>
          data.steps[key] ? (
            <li key={key} className="flex items-center gap-2 text-sm text-emerald-600">
              <span aria-hidden>✓</span> {labels[key]}
            </li>
          ) : (
            <li key={key}>
              <button
                className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 hover:underline"
                onClick={() => void goToDashboard(path).catch(() => undefined)}
              >
                <span className="text-gray-300" aria-hidden>○</span> {labels[key]}
              </button>
            </li>
          )
        )}
      </ul>
    </div>
  );
}
